import { useEffect, useState } from "react";
import api, { API, withCompany } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, CalendarDays, Eye, Pencil, FileDown } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import TimesheetFilters, { EMPTY_FILTERS } from "@/components/TimesheetFilters";
import TimesheetForm from "@/components/TimesheetForm";
import TimesheetDetail from "@/components/TimesheetDetail";
import { fmtDate, fmtNum, fmtTL } from "@/lib/utils";

const STATUS_COLOR = { tam: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", yarim: "bg-amber-500/15 text-amber-700 dark:text-amber-400", izinli: "bg-blue-500/15 text-blue-700 dark:text-blue-400", raporlu: "bg-rose-500/15 text-rose-700 dark:text-rose-400", gelmedi: "bg-slate-500/15 text-slate-700 dark:text-slate-300" };
const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

export default function Puantaj() {
  const [projects, setProjects] = useState([]);
  const [firms, setFirms] = useState([]);
  const [opts, setOpts] = useState({});
  const [f, setF] = useState(EMPTY_FILTERS);
  const [periodKey, setPeriodKey] = useState(0);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ open: false, record: null });
  const [detail, setDetail] = useState(null);
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => thisYear - i);

  const loadOpts = () => api.get("/timesheets/options").then(r => setOpts(r.data));
  const load = () => api.get("/timesheets", { params: clean(f) }).then(r => setRows(r.data));
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); api.get("/firms").then(r => setFirms(r.data)); loadOpts(); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [f]);

  const remove = async (t) => {
    if (!window.confirm(`${t.worker_name} — ${fmtDate(t.work_date)} kaydı silinsin mi?`)) return;
    await api.delete(`/timesheets/${t.id}`); toast.success("Silindi"); load();
  };
  const saved = () => { setForm({ open: false, record: null }); setDetail(null); load(); loadOpts(); };
  const reset = () => { setF(EMPTY_FILTERS); setPeriodKey(k => k + 1); };
  const tot = rows.reduce((a, t) => ({ h: a.h + t.hours_worked, o: a.o + t.overtime_hours, c: a.c + (t.cost || 0) }), { h: 0, o: 0, c: 0 });

  return (
    <div data-testid="timesheets-page">
      <PageHeader title="Puantaj" subtitle="Çalışanların şantiye, firma, iş kalemi ve blok kırılımlarıyla günlük takibi.">
        <Button onClick={() => setForm({ open: true, record: null })} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="new-timesheet-button"><Plus className="w-4 h-4 mr-1" /> Yeni Puantaj</Button>
      </PageHeader>
      <TimesheetFilters f={f} setF={setF} projects={projects} firms={firms} opts={opts} years={years} periodKey={periodKey} onReset={reset} />
      <Tabs defaultValue="records">
        <TabsList>
          <TabsTrigger value="records" data-testid="ts-tab-records">Kayıtlar ({rows.length})</TabsTrigger>
          <TabsTrigger value="report" data-testid="ts-tab-report">Rapor</TabsTrigger>
        </TabsList>
        <TabsContent value="records">
          <div className="flex gap-6 my-3 text-sm" data-testid="ts-summary">
            <span><span className="text-muted-foreground">Toplam saat:</span> <strong className="font-mono-nums">{fmtNum(tot.h)}</strong></span>
            <span><span className="text-muted-foreground">Mesai:</span> <strong className="font-mono-nums">{fmtNum(tot.o)}</strong></span>
            <span><span className="text-muted-foreground">Adam-gün:</span> <strong className="font-mono-nums">{fmtNum(tot.h / 8)}</strong></span>
            <span><span className="text-muted-foreground">İşçilik maliyeti:</span> <strong className="font-mono-nums text-amber-700 dark:text-amber-400">{fmtTL(tot.c)}</strong></span>
          </div>
          {rows.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-border p-12 text-center"><CalendarDays className="w-10 h-10 mx-auto text-muted-foreground mb-3" /><div className="font-semibold">Kayıt bulunamadı</div></div>
          ) : (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-muted/40"><TableHead>Tarih</TableHead><TableHead>Çalışan</TableHead><TableHead>Şantiye / Blok</TableHead><TableHead>Firma</TableHead><TableHead>İş Kalemi</TableHead>
                  <TableHead className="text-right">Saat</TableHead><TableHead className="text-right">Mesai</TableHead><TableHead className="text-right">Maliyet</TableHead><TableHead>Durum</TableHead><TableHead>Not</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>{rows.map(t => (
                  <TableRow key={t.id} data-testid={`ts-row-${t.id}`} className="cursor-pointer hover:bg-amber-500/5" onClick={() => setDetail(t)}>
                    <TableCell className="font-mono-nums text-xs">{fmtDate(t.work_date)}</TableCell>
                    <TableCell className="font-medium">{t.worker_name}<div className="text-[11px] text-muted-foreground">{opts.roles?.[t.role] || t.role}</div></TableCell>
                    <TableCell className="text-xs">{t.project_name}{t.block && <div className="text-muted-foreground">{t.block}</div>}</TableCell>
                    <TableCell className="text-xs max-w-[160px]">{t.firm_name || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[180px]">{t.work_item || "—"}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmtNum(t.hours_worked)}</TableCell>
                    <TableCell className="text-right font-mono-nums text-amber-600">{fmtNum(t.overtime_hours)}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmtTL(t.cost)}</TableCell>
                    <TableCell><Badge className={STATUS_COLOR[t.status]}>{opts.statuses?.[t.status] || t.status}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[160px] truncate">{t.notes}</TableCell>
                    <TableCell className="text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => setDetail(t)} data-testid={`ts-view-${t.id}`}><Eye className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setForm({ open: true, record: t })} data-testid={`ts-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`ts-delete-${t.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="report"><TimesheetReport filters={f} opts={opts} /></TabsContent>
      </Tabs>
      <TimesheetForm open={form.open} record={form.record} projects={projects} firms={firms} opts={opts} onClose={() => setForm({ open: false, record: null })} onSaved={saved} />
      {detail && <TimesheetDetail record={detail} opts={opts} onClose={() => setDetail(null)} onEdit={(r) => setForm({ open: true, record: r })} />}
    </div>
  );
}

function TimesheetReport({ filters, opts }) {
  const [g1, setG1] = useState("worker");
  const [g2, setG2] = useState("");
  const [data, setData] = useState(null);
  const groups = opts.groups || {};

  const params = () => {
    const p = new URLSearchParams(clean(filters));
    p.append("group_by", g1);
    if (g2 && g2 !== g1) p.append("group_by", g2);
    return p;
  };
  useEffect(() => { api.get(`/timesheets/report?${params()}`).then(r => setData(r.data)); }, [filters, g1, g2]);

  return (
    <div className="mt-3" data-testid="ts-report">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><div className="text-xs font-semibold text-muted-foreground mb-1">Gruplama</div>
          <Select value={g1} onValueChange={setG1}><SelectTrigger className="w-44" data-testid="ts-report-group1"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(groups).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <div><div className="text-xs font-semibold text-muted-foreground mb-1">Alt kırılım</div>
          <Select value={g2 || "__none__"} onValueChange={v => setG2(v === "__none__" ? "" : v)}><SelectTrigger className="w-44" data-testid="ts-report-group2"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="__none__">Yok</SelectItem>{Object.entries(groups).filter(([k]) => k !== g1).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
        <Button variant="outline" onClick={() => { window.location.href = withCompany(`${API}/reports/timesheets.xlsx?${params()}`); }} data-testid="ts-report-excel"
          className="border-emerald-600/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"><FileDown className="w-4 h-4 mr-1" /> Excel İndir</Button>
        <p className="text-xs text-muted-foreground">Üstteki filtreler rapora da uygulanır.</p>
      </div>
      {data && (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-muted/40">
              {data.labels.map(l => <TableHead key={l}>{l}</TableHead>)}
              <TableHead className="text-right">Kayıt</TableHead><TableHead className="text-right">Gün</TableHead><TableHead className="text-right">Çalışan</TableHead>
              <TableHead className="text-right">Saat</TableHead><TableHead className="text-right">Mesai</TableHead><TableHead className="text-right">Adam-Gün</TableHead>
              <TableHead className="text-right">Devamsızlık</TableHead><TableHead className="text-right">İşçilik Maliyeti</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.rows.map((r, i) => (
                <TableRow key={i} data-testid="ts-report-row">
                  {data.keys.map(k => <TableCell key={k} className="text-sm font-medium">{k === "day" ? fmtDate(r[k]) : r[k]}</TableCell>)}
                  <TableCell className="text-right font-mono-nums">{r.records}</TableCell><TableCell className="text-right font-mono-nums">{r.days}</TableCell>
                  <TableCell className="text-right font-mono-nums">{r.workers}</TableCell><TableCell className="text-right font-mono-nums">{fmtNum(r.hours)}</TableCell>
                  <TableCell className="text-right font-mono-nums">{fmtNum(r.overtime)}</TableCell><TableCell className="text-right font-mono-nums">{fmtNum(r.man_days)}</TableCell>
                  <TableCell className="text-right font-mono-nums">{r.absent}</TableCell><TableCell className="text-right font-mono-nums font-semibold">{fmtTL(r.cost)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-amber-500/10 font-bold" data-testid="ts-report-total">
                <TableCell colSpan={data.keys.length}>Toplam</TableCell>
                <TableCell className="text-right font-mono-nums">{data.totals.records}</TableCell><TableCell /><TableCell />
                <TableCell className="text-right font-mono-nums">{fmtNum(data.totals.hours)}</TableCell><TableCell className="text-right font-mono-nums">{fmtNum(data.totals.overtime)}</TableCell>
                <TableCell className="text-right font-mono-nums">{fmtNum(data.totals.man_days)}</TableCell><TableCell className="text-right font-mono-nums">{data.totals.absent}</TableCell>
                <TableCell className="text-right font-mono-nums">{fmtTL(data.totals.cost)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
